import { ISignType, SignType } from '@app/models/SignType'
import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export interface ISignTypesListState {
  readonly error?: string
  readonly filterText: string
  readonly isLoading: boolean
  readonly items: List<ISignType>
  readonly newSignType: ISignType
  readonly totalItems: number
}

const SignTypesListStateRecord = Record({
  error: undefined,
  filterText: '',
  isLoading: false,
  items: List<SignType>(),
  newSignType: undefined,
  totalItems: 0,
})

export class SignTypesListState extends SignTypesListStateRecord
  implements ISignTypesListState {
  public static fromJS(initialState?: Partial<ISignTypesListState>) {
    if (!initialState) {
      return new SignTypesListState()
    }

    const { items, ...rest } = initialState
    return new SignTypesListState({
      items: items ? List(items.map((i) => SignType.fromJS(i))) : undefined,
      ...rest,
    })
  }

  public readonly error?: string
  public readonly filterText: string
  public readonly isLoading: boolean
  public readonly items: List<SignType>
  public readonly newSignType: ISignType
  public readonly totalItems: number

  constructor(props?: Partial<SignTypesListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  ClearNewType = 'SIGN_TYPES_LIST::CLEAR_NEW_TYPE',
  EditSignType = 'SIGN_TYPES_LIST::EDIT_SIGN_TYPE',
  LoadSignTypes = 'SIGN_TYPES_LIST::LOAD_SIGN_TYPES',
  LoadSignTypesFailure = 'SIGN_TYPES_LIST::LOAD_SIGN_TYPES_FAILURE',
  LoadSignTypesRequest = 'SIGN_TYPES_LIST::LOAD_SIGN_TYPES_REQUEST',
  LoadSignTypesSuccess = 'SIGN_TYPES_LIST::LOAD_SIGN_TYPES_SUCCESS',
  RemoveSignType = 'SIGN_TYPES_LIST::REMOVE_SIGN_TYPE',
  SetFilterText = 'SIGN_TYPES_LIST::SET_FILTER_TEXT',
  SetSignName = 'SIGN_TYPES_LIST::SET_SIGN_NAME',
}

interface IClearNewTypeAction {
  type: ActionTypes.ClearNewType
}

interface IEditSignTypeAction {
  type: ActionTypes.EditSignType
  value: ISignType
}

interface ILoadSignTypesAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: ActionTypes.LoadSignTypes
}

interface ILoadSignTypesFailureAction {
  error: string
  type: ActionTypes.LoadSignTypesFailure
}

interface ILoadSignTypesRequestAction {
  type: ActionTypes.LoadSignTypesRequest
}

interface ILoadSignTypesSuccessAction {
  append: boolean
  items: SignType[]
  totalItems: number
  type: ActionTypes.LoadSignTypesSuccess
}

interface IRemoveSignTypeAction {
  type: ActionTypes.RemoveSignType
  value: ISignType
}

interface ISetFilterTextAction {
  filter: string
  type: ActionTypes.SetFilterText
}

interface ISetSignNameAction {
  id: string
  type: ActionTypes.SetSignName
  value: string
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IClearNewTypeAction
  | ILoadSignTypesFailureAction
  | ILoadSignTypesRequestAction
  | ILoadSignTypesSuccessAction
  | ISetFilterTextAction
  | ISetSignNameAction

// -------------------------
// ACTION CREATORS
// -------------------------
function clearNewType(): IClearNewTypeAction {
  return {
    type: ActionTypes.ClearNewType,
  }
}

function editSignType(value: ISignType): IEditSignTypeAction {
  return {
    type: ActionTypes.EditSignType,
    value,
  }
}

function loadSignTypes(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadSignTypesAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: ActionTypes.LoadSignTypes,
  }
}

function loadSignTypesFailure(error: string): ILoadSignTypesFailureAction {
  return {
    error,
    type: ActionTypes.LoadSignTypesFailure,
  }
}

function loadSignTypesRequest(): ILoadSignTypesRequestAction {
  return {
    type: ActionTypes.LoadSignTypesRequest,
  }
}

function loadSignTypesSuccess(
  items: SignType[],
  append: boolean,
  totalItems: number,
): ILoadSignTypesSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: ActionTypes.LoadSignTypesSuccess,
  }
}

function removeSignType(value: ISignType): IRemoveSignTypeAction {
  return {
    type: ActionTypes.RemoveSignType,
    value,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: ActionTypes.SetFilterText,
  }
}

function setSignName(value: string, id: string): ISetSignNameAction {
  return {
    id,
    type: ActionTypes.SetSignName,
    value,
  }
}

export const actionCreators = {
  clearNewType,
  editSignType,
  loadSignTypes,
  removeSignType,
  setFilterText,
  setSignName,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<SignTypesListState> = (
  state: SignTypesListState = new SignTypesListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.ClearNewType:
      return state.set('newSignType', undefined) as SignTypesListState

    case ActionTypes.LoadSignTypesFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as SignTypesListState

    case ActionTypes.LoadSignTypesRequest:
      return state.set('isLoading', true) as SignTypesListState

    case ActionTypes.LoadSignTypesSuccess:
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<SignType>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('totalItems', action.totalItems) as SignTypesListState

    case ActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as SignTypesListState

    case ActionTypes.SetSignName:
      const signTypeIndex = state.items.findIndex((item: any) => {
        return item.get('id') === action.id
      })

      if (signTypeIndex === -1) {
        return state.set(
          'newSignType',
          new SignType({ id: action.id, name: action.value }),
        ) as SignTypesListState
      }

      return state.setIn(
        ['items', signTypeIndex, 'name'],
        action.value,
      ) as SignTypesListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* editSignTypeSaga(action: IEditSignTypeAction) {
  try {
    const baseUrl = '/api/signTypes'

    const editOpts = {
      body: JSON.stringify(action.value),
      method:
        action.value.id === 'new-sign' ? RequestMethod.POST : RequestMethod.PUT,
    }

    const result = yield makeAuthenticatedRequest(baseUrl, editOpts)

    if (result.success === true) {
      yield put(
        successToast(
          'Success',
          `${result.entity.name} successfully ${
            action.value.id === 'new-sign' ? 'created' : 'updated'
          }`,
        ),
      )
      yield put(loadSignTypes(0, 20, false))
    } else {
      yield put(loadSignTypesFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadSignTypesFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadSignTypesSaga(action: ILoadSignTypesAction) {
  try {
    yield put(loadSignTypesRequest())

    const currentState: SignTypesListState = yield select(
      (state: ApplicationState) => state.signTypeList,
    )

    const query: string[] = []

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    const baseUrl = '/api/signTypes'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: SignType[] = result.results.map((c: any) =>
        SignType.fromJS(c),
      )
      yield put(loadSignTypesSuccess(contacts, action.append, 0))
    } else {
      yield put(loadSignTypesFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadSignTypesFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* removeSignTypeSaga(action: IRemoveSignTypeAction) {
  try {
    const baseUrl = `/api/signTypes/${action.value.id}`

    const deleteOpts = {
      method: RequestMethod.DELETE,
    }

    const result = yield makeAuthenticatedRequest(baseUrl, deleteOpts)

    if (result.success === true) {
      yield put(
        successToast('Success', `${action.value.name} successfully deleted`),
      )
      yield put(loadSignTypes(0, 20, false))
    } else {
      yield put(loadSignTypesFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadSignTypesFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchEditSignType() {
  yield takeLatest(ActionTypes.EditSignType, editSignTypeSaga)
}

function* watchLoadSignTypes() {
  yield takeLatest(ActionTypes.LoadSignTypes, loadSignTypesSaga)
}

function* watchRemoveSignType() {
  yield takeLatest(ActionTypes.RemoveSignType, removeSignTypeSaga)
}

export function* signTypeListRootSaga() {
  yield fork(watchEditSignType)
  yield fork(watchLoadSignTypes)
  yield fork(watchRemoveSignType)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState): SignTypesListState =>
  state.signTypeList

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectFilterText = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.filterText,
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.items,
  )

export const selectNewSignType = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.newSignType,
  )

export const selectTotalItems = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.totalItems,
  )
